/**
 * El testimoni d'aparell (OBR-25, pla §4.2, canvi C2 dels spikes de F0): el que `device-enrol` dona a
 * una tauleta en canviar-li un codi d'enrolament, i el que `pin-login` demana a cada entrada.
 *
 *     deviceSecret = deviceId (16 B) ‖ aleatori (32 B) ‖ etiqueta (16 B)
 *
 * en hexadecimal MINÚSCULA: 32 + 64 + 32 = 128 caràcters.
 *
 *   · `deviceId`  — l'`id` de la fila de `devices` que l'administrador ha creat o triat per enrolar.
 *   · `aleatori`  — 256 bits que genera `device-enrol` en el moment del canvi. És el SECRET de debò:
 *     la base de dades només en desa un resum (`secret_hash = HMAC(DEVICE_KEY, "secret:" ‖ aleatori)`).
 *   · `etiqueta`  — els primers 128 bits de `HMAC(DEVICE_KEY, "devmac:" ‖ deviceId ‖ aleatori)`.
 *
 * L'ETIQUETA NO ÉS L'AUTENTICADOR, és un escut contra la saturació. `pin-login` la comprova ABANS de
 * qualsevol crida a la plataforma (només CPU, en temps constant): un testimoni absent, mal format o
 * amb l'etiqueta falsa rep un `401 device_not_enrolled` barat i uniforme sense tocar la base de dades.
 * F0/S5 ho va mesurar: 150 req/s de testimonis falsos amb l'etiqueta → tot `401`, p95 219 ms i el
 * BackOffice intacte; comprovant el secret a la base de dades en canvi, p95 de 5,7 s per a tothom (la
 * base de dades de la branca tenia `max_connections = 30`). L'autenticador és el resum de la base de
 * dades (i `revoked_at`), que `pin_attempt` comprova: qui conegués `DEVICE_KEY` però no la base de
 * dades no coneix l'aleatori.
 *
 * Aquest mòdul és PUR: només dona la FORMA del testimoni i els missatges que cal signar. L'HMAC el fa la
 * funció edge (WebCrypto), perquè `shared` no guanyi cap dependència; les dues funcions que el fan
 * servir (`device-enrol`, `pin-login`) en porten una còpia i un test de paritat la vigila.
 */

/** Caràcters hexadecimals de cada tros del testimoni. */
export const DEVICE_ID_HEX_LENGTH = 32;
export const DEVICE_RANDOM_HEX_LENGTH = 64;
export const DEVICE_TAG_HEX_LENGTH = 32;
/** El testimoni sencer: 128 caràcters. */
export const DEVICE_SECRET_HEX_LENGTH =
  DEVICE_ID_HEX_LENGTH + DEVICE_RANDOM_HEX_LENGTH + DEVICE_TAG_HEX_LENGTH;

/** Prefix de domini de l'etiqueta: amb la mateixa clau, un altre ús donaria un altre resum. */
export const DEVICE_TAG_CONTEXT = 'devmac:';
/** Prefix de domini del resum que es desa a `device_credentials.secret_hash`. */
export const DEVICE_SECRET_HASH_CONTEXT = 'secret:';

const LOWER_HEX = /^[0-9a-f]+$/;
const CANONICAL_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

/** Un testimoni desglossat. */
export interface ParsedDeviceSecret {
  /** L'`id` de l'aparell com a UUID canònic (minúscula, amb guionets). */
  readonly deviceId: string;
  readonly deviceIdHex: string;
  readonly randomHex: string;
  readonly tagHex: string;
}

/** `true` si `text` té la FORMA d'un testimoni: exactament 128 hexadecimals en minúscula. */
export function isDeviceSecretShape(text: string): boolean {
  return text.length === DEVICE_SECRET_HEX_LENGTH && LOWER_HEX.test(text);
}

/** Els 32 hexadecimals d'un UUID canònic, o `null` si no ho és. */
export function uuidToHex(uuid: string): string | null {
  return CANONICAL_UUID.test(uuid) ? uuid.replace(/-/g, '') : null;
}

/** L'UUID canònic de 32 hexadecimals en minúscula, o `null` si no ho són. */
export function hexToUuid(hex: string): string | null {
  if (hex.length !== DEVICE_ID_HEX_LENGTH || !LOWER_HEX.test(hex)) return null;
  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20, 32),
  ].join('-');
}

/**
 * Desglossa un testimoni. Estricte: només minúscula i exactament 128 caràcters; tot el que no encaixi
 * dona `null` (qui crida respon `401 device_not_enrolled` o `400`, segons on sigui).
 */
export function parseDeviceSecret(text: string): ParsedDeviceSecret | null {
  if (!isDeviceSecretShape(text)) return null;
  const deviceIdHex = text.slice(0, DEVICE_ID_HEX_LENGTH);
  const randomHex = text.slice(
    DEVICE_ID_HEX_LENGTH,
    DEVICE_ID_HEX_LENGTH + DEVICE_RANDOM_HEX_LENGTH,
  );
  const tagHex = text.slice(DEVICE_ID_HEX_LENGTH + DEVICE_RANDOM_HEX_LENGTH);
  const deviceId = hexToUuid(deviceIdHex);
  if (deviceId === null) return null;
  return { deviceId, deviceIdHex, randomHex, tagHex };
}

/** El missatge que se signa per obtenir l'etiqueta: `"devmac:" ‖ deviceId ‖ aleatori` (hexadecimals). */
export function deviceTagMessage(deviceIdHex: string, randomHex: string): string {
  return `${DEVICE_TAG_CONTEXT}${deviceIdHex}${randomHex}`;
}

/** El missatge que se signa per obtenir el resum que es desa: `"secret:" ‖ aleatori` (hexadecimal). */
export function deviceSecretHashMessage(randomHex: string): string {
  return `${DEVICE_SECRET_HASH_CONTEXT}${randomHex}`;
}

/**
 * Munta el testimoni a partir de l'`id` de l'aparell, l'aleatori i l'etiqueta ja calculada.
 *
 * @throws RangeError si alguna peça no té la forma que toca (no es munta mai un testimoni a mitges).
 */
export function composeDeviceSecret(deviceId: string, randomHex: string, tagHex: string): string {
  const deviceIdHex = uuidToHex(deviceId);
  if (deviceIdHex === null) {
    throw new RangeError('composeDeviceSecret: deviceId must be a canonical lowercase UUID');
  }
  if (randomHex.length !== DEVICE_RANDOM_HEX_LENGTH || !LOWER_HEX.test(randomHex)) {
    throw new RangeError('composeDeviceSecret: random must be 64 lowercase hex characters');
  }
  if (tagHex.length !== DEVICE_TAG_HEX_LENGTH || !LOWER_HEX.test(tagHex)) {
    throw new RangeError('composeDeviceSecret: tag must be 32 lowercase hex characters');
  }
  return `${deviceIdHex}${randomHex}${tagHex}`;
}
